"use client";
import { useTranslation } from "../../translations/index.js";
import { useMemo, useState, useCallback } from "react";
import "../validation/validators.js";
import { createObjectValidator, initialUserInput, getFieldNecessities } from "../validation/validation.js";
import { makePropertySetter, useFormHelpers, makeBlurSetter } from "./formHelpers.js";
function getDefaultNecessityIndicator(validators, values) {
  let optionalFields = 0;
  let requiredFields = 0;
  for (const fieldName in validators) {
    const necessity = validators[fieldName].necessity;
    const necessityConfig = typeof necessity === "function" ? necessity(values) : necessity;
    if (necessityConfig.isRequired) {
      requiredFields++;
    } else {
      optionalFields++;
    }
  }
  return optionalFields < requiredFields ? "optional" : "required";
}
function getNecessityText(translation, showNecessityOn, formNecessity) {
  const t = translation.form.fieldNecessityPreamble;
  if (formNecessity === "mixed") {
    return showNecessityOn === "optional" ? t.allFieldsRequiredUnless : t.allFieldsOptionalUnless;
  }
  return formNecessity === "optional" ? t.allFieldsOptional : t.allFieldsRequired;
}
function useLocalFormState(initialState, validators, validationOptions) {
  let rawState = initialState;
  const defaultOptions = {
    showNecessityOn: getDefaultNecessityIndicator(validators, rawState)
  };
  const options = { ...defaultOptions, ...validationOptions };
  const validator = useMemo(() => createObjectValidator(validators), [validators]);
  const [state, setState] = useState(initialUserInput(initialState, validator));
  rawState = state.raw;
  const formNecessity = useMemo(() => getFieldNecessities(state.raw, validators), [state.raw, validators]);
  const translation = useTranslation();
  const necessityText = getNecessityText(translation, options.showNecessityOn, formNecessity);
  const setField = useMemo(() => makePropertySetter(validator), [validator]);
  const setBlurred = makeBlurSetter();
  const showAllErrors = useCallback(() => {
    setState((prev) => {
      const validated = validator(prev.raw);
      const showError = {};
      for (const key of Object.keys(prev.raw)) {
        const field = key;
        showError[field] = !validated.validation[field].isValid;
      }
      return {
        ...prev,
        ...validated,
        showError
      };
    });
  }, [validator]);
  const reset = useCallback((...fields) => {
    if (fields.length === 0) {
      setState(initialUserInput(initialState, validator));
      return;
    }
    setState((prev) => fields.reduce((result, field) => setField(result, [field, initialState[field]]), prev));
  }, [initialState, setField, validator]);
  const { inputProps, setValue } = useFormHelpers(state, validators, ([field, value]) => setState((prev) => setField(prev, [field, value])), (field) => setState((prev) => setBlurred(prev, field)));
  return {
    validationConfig: {
      validators,
      options
    },
    state,
    setValue,
    inputProps,
    showAllErrors,
    necessityText,
    reset
  };
}
export {
  useLocalFormState
};
